import { Button, IconButton, Modal, Tooltip } from '@dev-spendesk/grapes';
import { useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import { useSetSpanishDprSettings } from 'src/core/modules/bookkeep/apis/useSetSpanishDprSettings';

import { SpanishDprDateSelector } from './SpanishDprDateSelector';
import { getInterval, getIntervalLabel } from './helpers/interval';

export function SpanishDprUpdateEndYeadDate({
  initialMonth,
  initialDay,
  isEditable,
}: {
  initialMonth: number;
  initialDay: number;
  isEditable: boolean;
}) {
  const { t } = useTranslation('global');
  const [editDateModalOpen, setEditDateModalOpen] = useState(false);
  const [updateSpanishDprSettings] = useSetSpanishDprSettings();
  const { pushNotif } = useNotifications();
  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    initialMonth,
  );
  const [selectedDay, setSelectedDay] = useState<number | null>(initialDay);
  const monthOptionsMap = useMemo(() => {
    return [
      t('misc.months.jan'),
      t('misc.months.feb'),
      t('misc.months.mar'),
      t('misc.months.apr'),
      t('misc.months.may'),
      t('misc.months.jun'),
      t('misc.months.jul'),
      t('misc.months.aug'),
      t('misc.months.sep'),
      t('misc.months.oct'),
      t('misc.months.nov'),
      t('misc.months.dec'),
    ];
  }, [t]);

  const yearInterval = getInterval(initialMonth, initialDay);
  const isAvailableToSetValue = selectedMonth !== null && selectedDay !== null;

  const handleSave = async () => {
    if (!isAvailableToSetValue) {
      return;
    }
    await updateSpanishDprSettings({
      fiscalYearEndMonth: selectedMonth,
      fiscalYearEndDay: selectedDay,
    });
    setEditDateModalOpen(false);
    pushNotif({
      type: NotificationType.Success,
      message: t('bookkeep.settings.spanishDpr.setupDate.successNotif'),
    });
  };

  return (
    <>
      <div className="rounded-8 bg-background-secondary-default body-m text-content-secondary-bg-secondary stretch my-24 flex items-center gap-8 p-16">
        <span className="body-m flex-1">
          <span className="text-content-secondary-bg-secondary">
            {t(
              'bookkeep.settings.spanishDpr.dateSelector.fiscalYearForArchival',
            )}
          </span>
          <span className="title-m text-content-primary ml-4">
            {getIntervalLabel(
              yearInterval.start,
              yearInterval.end,
              monthOptionsMap,
            )}
          </span>
        </span>
        {isEditable ? (
          <IconButton
            variant="tertiaryNeutral"
            iconName="pen"
            aria-label="Edit date"
            onClick={() => {
              setEditDateModalOpen(true);
            }}
          />
        ) : (
          <Tooltip
            content={t(
              'bookkeep.settings.spanishDpr.editYearEndModal.disabledTooltip',
            )}
            placement="top"
          >
            <IconButton
              variant="tertiaryNeutral"
              iconName="pen"
              aria-label="Edit date"
              isDisabled
            />
          </Tooltip>
        )}
      </div>
      <Modal
        isOpen={editDateModalOpen}
        onClose={() => setEditDateModalOpen(false)}
        title={t('bookkeep.settings.spanishDpr.editYearEndModal.title')}
        iconName="calendar"
        actions={[
          <Button
            key="cancel"
            variant="secondaryNeutral"
            text={t('misc.cancel')}
            onClick={() => setEditDateModalOpen(false)}
          />,
          <Button
            key="save"
            isDisabled={!isAvailableToSetValue}
            variant="primaryBrand"
            text={t('misc.saveChanges')}
            onClick={handleSave}
          />,
        ]}
      >
        {t('bookkeep.settings.spanishDpr.editYearEndModal.content')}
        <SpanishDprDateSelector
          selectedMonth={selectedMonth}
          selectedDay={selectedDay}
          setSelectedMonth={setSelectedMonth}
          setSelectedDay={setSelectedDay}
        />
      </Modal>
    </>
  );
}
